import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IAction } from 'app/_models/IAction';
import { IParameter } from 'app/_models/IParameter';
import { AppComponent } from 'app/app.component';
import { ISettings } from 'app/_models/ISettings';
import { IconDefinition, faCalendar, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.css'],
})
export class CustomDatePickerComponent implements OnInit {
  //#region Fields & Icons
  @Input() settings: ISettings;  
  @Input() param: IParameter;
  @Input() filterFromValue: string;
  @Input() filterToValue: string;
  @Input() rule: any;
  @Input() action: IAction;
  @Output() dateSelected = new EventEmitter<string>();
  selectedDate: string = '';
  isCalendarVisible: boolean = false;
  currentDate: Date = new Date();
  currentMonth!: number;
  currentYear!: number;
  days: Date[] = [];
  dayNames: string[] = [];
  monthNames: string[] = [];
  yearRange: number[] = [];
  viewMode: 'days' | 'months' | 'years' = 'days';
  minYear = new Date().getFullYear() - 100;
  maxYear = new Date().getFullYear() + 100;
  faCalendar: IconDefinition = faCalendar;
  faArrowRight: IconDefinition = faArrowRight;
  faArrowLeft: IconDefinition = faArrowLeft;
  //#endregion

  constructor(public app: AppComponent) { }

  ngOnInit(): void {    
    this.dayNames = [
      this.app.translations.LBL_Sunday,
      this.app.translations.LBL_Monday,
      this.app.translations.LBL_Tuesday,
      this.app.translations.LBL_Wednesday,
      this.app.translations.LBL_Thursday,
      this.app.translations.LBL_Friday,
      this.app.translations.LBL_Saturday      
    ];

    this.monthNames = [
      this.app.translations.LBL_Januray,
      this.app.translations.LBL_February,
      this.app.translations.LBL_March,
      this.app.translations.LBL_April,
      this.app.translations.LBL_May,
      this.app.translations.LBL_June,
      this.app.translations.LBL_July,
      this.app.translations.LBL_August,
      this.app.translations.LBL_September,
      this.app.translations.LBL_October,
      this.app.translations.LBL_November,
      this.app.translations.LBL_December,
    ];

    this.currentMonth = this.currentDate.getMonth();
    this.currentYear = this.currentDate.getFullYear();
    this.generateCalendar();
    this.generateYearRange();
    
    if (this.param && this.param.dateValues.userDate) {
      if (this.param.dateValues.userDate.includes(' ')) 
        this.selectedDate = this.param.dateValues.userDate.split(' ')[0]; 
      else     
        this.selectedDate = this.param.dateValues.userDate;
    }
      
    if (this.filterFromValue) this.selectedDate = this.filterFromValue;
    if (this.filterToValue) this.selectedDate = this.filterToValue;
    if (this.rule && this.rule.value) this.selectedDate = this.rule.value;
  }  

  showYearView(): void {
    this.viewMode = 'years';
    this.generateYearRange();
  }

  selectYear(year: number): void {
    this.currentYear = year;
    this.showMonthView();
  }

  showMonthView(): void { 
    this.viewMode = 'months'; 
  }

  selectMonth(month: number): void {
    this.currentMonth = month;
    this.showDayView();
  }

  showDayView(): void {
    this.viewMode = 'days';
    this.generateCalendar();
  }

  selectDay(day: Date): void {
    if (!(day.getMonth() === this.currentMonth)) return;    
    this.selectedDate = this.formatDay(day);
    this.dateSelected.emit(this.formatDate(day));
    this.isCalendarVisible = false;
  }

  selectToday(): void {
    let date = new Date();
    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.selectedDate = this.formatDay(date);
    this.dateSelected.emit(this.formatDate(date));
    this.isCalendarVisible = false;    
  }

  previous(): void {
    if (this.viewMode === 'days') {
      this.currentMonth--;
      if (this.currentMonth < 0) {
        this.currentMonth = 11;
        this.currentYear--;
      }
      this.generateCalendar();
    } else if (this.viewMode === 'months') {
      this.currentYear--;
    } else if (this.viewMode === 'years') {
      this.currentYear = Math.max(this.minYear, this.currentYear - 20);
      this.generateYearRange();
    }
  }

  next(): void {
    if (this.viewMode === 'days') {
      this.currentMonth++;
      if (this.currentMonth > 11) {
        this.currentMonth = 0;
        this.currentYear++;
      }
      this.generateCalendar();
    } else if (this.viewMode === 'months') {
      this.currentYear++;
    } else if (this.viewMode === 'years') {
      this.currentYear = Math.min(this.maxYear - 19, this.currentYear + 20);
      this.generateYearRange();
    }
  }

  generateCalendar(): void {
    this.days = [];    
    let daysFromPrevMonth = new Date(this.currentYear, this.currentMonth, 1).getDay(); 
    let daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();    
    let totalCells = Math.ceil((daysFromPrevMonth + daysInMonth) / 7) * 7;
    let daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;
    
    for (let i = 0; i < daysFromPrevMonth; i++) this.days.push(null);    
    for (let i = 1; i <= daysInMonth; i++) this.days.push(new Date(this.currentYear, this.currentMonth, i));    
    for (let i = 0; i < daysFromNextMonth; i++) this.days.push(null);
  }

  generateYearRange(): void {
    this.yearRange = [];
    let startYear = Math.max(this.minYear, Math.floor(this.currentYear / 20) * 20);
    for (let i = 0; i < 20 && startYear + i <= this.maxYear; i++) this.yearRange.push(startYear + i);
  }

  updateSelectedDate(value: string): void {    
    if (value.length == 0) {
      this.dateSelected.emit('null');
      this.isCalendarVisible = false;
      return;
    }

    let match;
    if ((match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})$/)) || (match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/))) {
      //value is in user format needs to be formatted to server format

      let dateValues = value.split('/');
      let serverDate;

      if (this.settings.dateFormatSetting == 'dd/MM/yyyy') serverDate = `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`;
      else if (this.settings.dateFormatSetting == 'yyyy/MM/dd') serverDate = `${dateValues[1]}/${dateValues[2]}/${dateValues[0]}`;
      else serverDate = value;

      this.dateSelected.emit(serverDate);
      this.isCalendarVisible = false;
    }
  }

  formatDay(date: Date): string {
    let year = date.getFullYear();
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let day = String(date.getDate()).padStart(2, '0');
    if (this.settings.dateFormatSetting == 'dd/MM/yyyy') return `${day}/${month}/${year}`;
    if (this.settings.dateFormatSetting == 'yyyy/MM/dd') return `${year}/${month}/${day}`;
    return `${month}/${day}/${year}`;
  }

  formatDate(date: Date): string {
    let year = date.getFullYear();
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let day = String(date.getDate()).padStart(2, '0');    
    return `${month}/${day}/${year}`;
  }
  
  toggleCalendar(): void {
    this.isCalendarVisible = !this.isCalendarVisible;
    if (this.isCalendarVisible) {
      this.viewMode = 'days';
      this.generateCalendar();
    }
  }  
}